import { ACCOUNT_WORKFLOW_STATUSES, AccountWorkflowStatus, PartnerTypeLabel } from 'vopay';

/** Statuses an account holds once it has left (or been pulled out of) the onboarding pipeline. */
export const TERMINAL_STATUSES: readonly AccountWorkflowStatus[] = [
  'Active',
  'Disabled',
  'Rejected',
  'Blocked',
  'Suspended',
];

/**
 * The onboarding stages an account created under this partner passes through on its way to Active,
 * in the order earthnode moves it (`VIOAccountWorkflowStageHelper::getNextStatus`, CA flow). These
 * tiles always render — zero included — so the partner's pipeline reads as a whole; the summary card
 * appends any other populated status after them, so nothing with accounts in it is ever hidden.
 *
 * `isPartnerLiability` mirrors the workflow's `IsVopayLiability` inverse: under VoPay liability an
 * account passes through Certification Pending before Active; under partner liability it activates
 * straight out of Account Provisioning.
 *
 * No country split: `getUsNextStatus` walks the same forward path as the CA flow. The US-only
 * statuses are exceptions, not stages — Third Party Review is where a US account lands when it is
 * rejected out of Underwriting Review (it then returns to Underwriting), and Finance Review has no
 * transition into it any more — so they surface only through the card's "populated status" fallback.
 */
export default function getOnboardingPipeline(
  partnerTypeLabel: PartnerTypeLabel,
  isPartnerLiability: boolean,
): AccountWorkflowStatus[] {
  // Application Pending → Underwriting Review → Credit Risk Review → Account Provisioning
  const applicationReview: AccountWorkflowStatus[] = [
    'Application Pending',
    'Underwriting Review',
    'Credit Risk Review',
    'Account Provisioning',
  ];

  const certification: AccountWorkflowStatus[] = isPartnerLiability ? [] : ['Certification Pending'];

  switch (partnerTypeLabel) {
    // VAR (non-reporting) accounts start at Application Pending and activate out of Account Provisioning.
    case 'VAR - PaaS':
    case 'VAR - PayFac (Non-Reporting)':
      return applicationReview;
    // PartnerReportingLiability: the account starts, and activates, at Account Provisioning.
    case 'VAR - PayFac (Reporting)':
      return ['Account Provisioning'];
    case 'Technology Partner':
      return ['Account Provisioning', ...certification];
    // Referral accounts start at Pre-Application Check and skip the agreement step after Manager Approval.
    case 'Referral ISO':
    case 'Referral ISV':
    case 'Referral - ISV RevShare':
      return [
        'Pre-Application Check',
        'Account Review and Validation',
        'Manager Approval',
        ...applicationReview,
        ...certification,
      ];
    // Inside Sales runs the full path, agreement included, and always certifies before Active.
    case 'Inside Sales':
      return [
        'Pre-Application Check',
        'Account Review and Validation',
        'Manager Approval',
        'Agreement Pending',
        ...applicationReview,
        'Certification Pending',
      ];
    // Unknown or not-yet-loaded partner type: no fixed pipeline; populated statuses still show.
    default:
      return [];
  }
}

/**
 * The statuses the overview's stage bar shows and the Accounts list's Status filter offers — the same
 * list, in the same order (ID-13964 D3): the partner's pipeline stages (zero included), then any other
 * onboarding status that holds accounts, then the statuses an account ends up in. A partner type with
 * no fixed pipeline shows every status that holds accounts, so no account is unreachable.
 */
export function getAccountStatusTiles(
  partnerTypeLabel: PartnerTypeLabel,
  isPartnerLiability: boolean,
  statusCounts: Partial<Record<AccountWorkflowStatus, number>>,
): AccountWorkflowStatus[] {
  const pipeline = getOnboardingPipeline(partnerTypeLabel, isPartnerLiability);

  const populatedElsewhere = ACCOUNT_WORKFLOW_STATUSES.filter(
    (status) => !pipeline.includes(status) && !TERMINAL_STATUSES.includes(status) && (statusCounts[status] ?? 0) > 0,
  );

  return [...pipeline, ...populatedElsewhere, ...TERMINAL_STATUSES];
}
